<script setup>
defineOptions({ name: "StatusPage" });
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useSeo } from "@/composables/useSeo";
import api from "@/services/api";

useSeo({
  title: "Status | Kali Sistemas",
  description: "Status público dos serviços do ecossistema Kali.",
});

const CHECK_INTERVAL_MS = 20000;
const WINDOW_MS = 4 * 60 * 60 * 1000;
const SLOT_MS = 5 * 60 * 1000;
const SLOT_COUNT = 48;
const AGG_WINDOW_MS = 5 * 60 * 1000;

const service = {
  name: "Kali Gestão Web",
};

const nowMs = ref(Date.now());
const status = ref("loading");
const checkedAt = ref(null);
const uptime30dPct = ref(null);
const points = ref([]);
const barsEl = ref(null);
const hover = ref(null);

const statusText = computed(() => {
  return (
    {
      online: "LIGADO",
      unstable: "INSTÁVEL",
      offline: "FORA",
      loading: "VERIFICANDO",
    }[effectiveStatus.value] || "VERIFICANDO"
  );
});

const statusBadgeClass = computed(() => {
  return (
    {
      online: "bg-[#22c55e]/10 text-emerald-700 ring-[#22c55e]/25",
      unstable: "bg-amber-500/10 text-amber-700 ring-amber-500/25",
      offline: "bg-red-500/10 text-red-700 ring-red-500/25",
      loading: "bg-slate-500/10 text-slate-600 ring-slate-400/25",
    }[effectiveStatus.value] ||
    "bg-slate-500/10 text-slate-600 ring-slate-400/25"
  );
});

const dotClass = computed(() => {
  return (
    {
      online: "bg-[#22c55e]",
      unstable: "bg-amber-400",
      offline: "bg-red-500",
      loading: "bg-slate-400",
    }[effectiveStatus.value] || "bg-slate-400"
  );
});

const checkedAtLabel = computed(() => {
  if (!checkedAt.value) return "—";
  return checkedAt.value.toLocaleString("pt-BR");
});

const uptimeLabel = computed(() => {
  const pct = uptime30dPct.value;
  return typeof pct === "number" ? `${pct.toFixed(2)}%` : "--";
});

const uptimePillClass = computed(() => {
  if (typeof uptime30dPct.value !== "number") {
    return "bg-slate-500/10 text-slate-600 ring-slate-400/25";
  }
  if (uptime30dPct.value >= 99.9) {
    return "bg-[#22c55e]/10 text-emerald-700 ring-[#22c55e]/25";
  }
  if (uptime30dPct.value >= 99) {
    return "bg-emerald-500/10 text-emerald-700 ring-emerald-500/25";
  }
  if (uptime30dPct.value >= 95) {
    return "bg-amber-500/10 text-amber-700 ring-amber-500/25";
  }
  return "bg-red-500/10 text-red-700 ring-red-500/25";
});

const normalizeStatus = (value) => {
  const v = String(value || "").toLowerCase();
  if (v === "online" || v === "unstable" || v === "offline") return v;
  return "unknown";
};

const effectiveStatus = computed(() => {
  const windowEnd = nowMs.value;
  const windowStart = windowEnd - AGG_WINDOW_MS;
  const recent = points.value
    .filter(
      (p) => typeof p?.t === "number" && p.t >= windowStart && p.t <= windowEnd,
    )
    .map((p) => normalizeStatus(p.status));

  if (recent.includes("offline")) return "offline";
  if (recent.includes("unstable")) return "unstable";
  if (recent.includes("online")) return "online";

  const fallback = normalizeStatus(status.value);
  return fallback === "unknown" ? "loading" : fallback;
});

const labelForStatus = (value) => {
  return (
    {
      online: "LIGADO",
      unstable: "INSTÁVEL",
      offline: "FORA",
      unknown: "SEM DADOS",
    }[String(value || "").toLowerCase()] || "SEM DADOS"
  );
};

const historySlots = computed(() => {
  const windowEnd = nowMs.value;
  const windowStart = windowEnd - WINDOW_MS;
  const sorted = [...points.value]
    .filter(
      (p) => typeof p?.t === "number" && p.t >= windowStart && p.t <= windowEnd,
    )
    .sort((a, b) => a.t - b.t);

  const slots = [];
  let cursor = 0;
  let lastStatus = "unknown";

  for (let i = 0; i < SLOT_COUNT; i += 1) {
    const slotEnd = windowStart + (i + 1) * SLOT_MS;
    while (cursor < sorted.length && sorted[cursor].t <= slotEnd) {
      lastStatus = normalizeStatus(sorted[cursor].status);
      cursor += 1;
    }

    const labelTime = new Date(slotEnd).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    slots.push({
      status: lastStatus,
      labelTime,
    });
  }

  return slots;
});

const barsGridStyle = computed(() => {
  const len = historySlots.value.length || SLOT_COUNT;
  return { gridTemplateColumns: `repeat(${len}, minmax(0, 1fr))` };
});

const barClassFor = (barStatus) => {
  return (
    {
      online: "bg-[#22c55e]/80",
      unstable: "bg-amber-400/80",
      offline: "bg-red-500/70",
      unknown: "bg-slate-200/80",
    }[barStatus] || "bg-slate-200/80"
  );
};

const onBarEnter = (event, bar) => {
  const el = barsEl.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const x = event?.clientX != null ? event.clientX - rect.left : rect.width / 2;
  const pct = rect.width ? (x / rect.width) * 100 : 50;
  const safePct = Math.max(2, Math.min(98, pct));

  hover.value = {
    pct: safePct,
    status: bar?.status,
    time: bar?.labelTime,
  };
};

const onBarsLeave = () => {
  hover.value = null;
};

const fetchPublicStatus = async () => {
  const res = await api.get("/status/public", {
    params: { _ts: Date.now() },
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  const data = res?.data || {};
  status.value = data.status || "offline";
  checkedAt.value = data.checked_at ? new Date(data.checked_at) : new Date();
  uptime30dPct.value =
    typeof data.uptime_30d_pct === "number" ? data.uptime_30d_pct : null;
};

const fetchPublicHistory = async () => {
  const res = await api.get("/status/public/history", {
    params: { _ts: Date.now() },
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  const data = res?.data || {};
  const list = Array.isArray(data.points) ? data.points : [];
  points.value = list
    .filter((p) => typeof p?.checked_at === "string")
    .map((p) => ({
      t: new Date(p.checked_at).getTime(),
      status: p.status,
    }))
    .slice(-240);
};

const refresh = async () => {
  nowMs.value = Date.now();

  try {
    await fetchPublicStatus();
  } catch {
    status.value = "offline";
    checkedAt.value = new Date();
    uptime30dPct.value = null;
  }

  try {
    await fetchPublicHistory();
  } catch {
    points.value = [];
  }
};

let intervalId = null;

onMounted(() => {
  refresh();
  intervalId = window.setInterval(() => {
    refresh();
  }, CHECK_INTERVAL_MS);
});

onUnmounted(() => {
  if (intervalId) window.clearInterval(intervalId);
});
</script>

<template>
  <div class="min-h-screen bg-slate-50 text-slate-900">
    <div class="px-4 pt-16 pb-20 mx-auto max-w-6xl sm:px-6 lg:px-8">
      <p class="text-xs font-bold tracking-widest uppercase text-slate-500">
        Status
      </p>
      <h1 class="mt-2 text-4xl font-extrabold tracking-tight text-slate-900">
        Serviços
      </h1>
      <p class="mt-3 text-sm font-semibold text-slate-500">
        Atualiza a cada 20 segundos (consolidado em 5 minutos)
      </p>

      <div
        class="px-6 py-5 mt-10 bg-white rounded-2xl border shadow-sm border-slate-200"
      >
        <div class="flex flex-col gap-5 sm:flex-row sm:items-center">
          <div class="flex gap-4 items-center">
            <span
              class="inline-flex items-center px-3 py-1 text-xs font-extrabold rounded-full ring-1 ring-inset"
              :class="uptimePillClass"
            >
              {{ uptimeLabel }}
            </span>

            <div class="flex gap-2 items-center">
              <span class="w-2.5 h-2.5 rounded-full" :class="dotClass"></span>
              <span class="text-sm font-extrabold text-slate-900">
                {{ service.name }}
              </span>
            </div>
          </div>

          <div class="flex-1">
            <div class="flex gap-4 justify-between items-center">
              <div class="text-xs font-semibold text-slate-500">
                Última verificação: {{ checkedAtLabel }}
              </div>
              <span
                class="inline-flex items-center px-3 py-1 text-xs font-extrabold rounded-lg ring-1 ring-inset"
                :class="statusBadgeClass"
              >
                {{ statusText }}
              </span>
            </div>

            <div class="relative mt-3" @mouseleave="onBarsLeave">
              <div ref="barsEl" class="grid gap-[2px]" :style="barsGridStyle">
                <span
                  v-for="(bar, idx) in historySlots"
                  :key="idx"
                  class="h-4 rounded-md"
                  :class="barClassFor(bar.status)"
                  @mouseenter="(e) => onBarEnter(e, bar)"
                ></span>
              </div>

              <div
                v-if="hover"
                class="absolute -top-16 z-10 pointer-events-none"
                :style="{
                  left: `${hover.pct}%`,
                  transform: 'translateX(-50%)',
                }"
              >
                <div
                  class="px-4 py-2 text-center rounded-xl ring-1 shadow-lg bg-slate-900 ring-black/5"
                >
                  <div class="text-sm font-extrabold text-white">
                    {{ labelForStatus(hover.status) }}
                  </div>
                  <div class="text-xs font-semibold text-slate-300">
                    {{ hover.time }}
                  </div>
                </div>
              </div>
            </div>

            <div
              class="mt-2 flex items-center justify-between text-[11px] font-semibold text-slate-500"
            >
              <span>4h</span>
              <span>atrás</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
